import React, { useEffect, useState } from 'react';
import { useAppSelector, useAppDispatch } from 'flow/hooks/useRedux';
import { generatePath, useHistory, useParams } from 'react-router-dom';
import { fromJS, Map } from 'immutable';

import { updateProfiles } from 'flow/actions/channels-actions';
import Loading from 'flow/components/ui-components/Loading';
import Page from 'flow/components/Setting/Page';
import Button from 'flow/components/ui-components/Button';
import CharacterCountInput from 'flow/components/ui-components/CharacterCountInput';
import useAppContext from 'flow/hooks/useAppContext';
import { PbxParams } from 'flow/types/Params';
import { routes } from 'flow/types/routes';

import { isEmptyNullOrUndefined } from 'flow/utils';
import {
    operatorNoteIsOverLimit,
    OPERATOR_NOTE_COUNT_FROM,
    OPERATOR_NOTE_MAX_LENGTH,
} from 'flow/utils/constants/profile-constants';

const PbxLiveChatGreeting = () => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const history = useHistory();
    const { channelKey } = useParams<PbxParams>();

    const channel = useAppSelector((state) =>
        state.channels.getIn(['channels', channelKey], Map()),
    );

    const [profiles, setProfiles] = useState(channel.get('profiles'));

    useEffect(() => {
        setProfiles(channel.get('profiles'));
    }, [channel, channelKey]);

    if (!profiles) {
        return <Loading />;
    }

    // Every profile goes up in one request, so a single over-limit greeting —
    // including one the user never touched — fails the whole save.
    const anyGreetingIsOverLimit = profiles.some((profile) =>
        operatorNoteIsOverLimit(profile.get('operatorNote')),
    );

    const handleSubmit = () => {
        dispatch(
            updateProfiles(
                channelKey,
                profiles.map((profile) =>
                    fromJS({
                        key: profile.get('key'),
                        operatorNote: isEmptyNullOrUndefined(
                            profile.get('operatorNote'),
                        )
                            ? ''
                            : profile.get('operatorNote'),
                    }),
                ),
            ),
        );
    };

    return (
        <Page
            header={i18next.t('Channel.Settings.Greeting')}
            onClickBack={() =>
                history.push(
                    generatePath(routes['pbx.channels.channelKey.settings'], {
                        channelKey,
                    }),
                )
            }
        >
            <div className="flex flex-col gap-8">
                <div className="flex items-center">
                    {i18next.t('Channel.Settings.Greeting.Description')}
                </div>

                {profiles.map((profile, index) => (
                    <CharacterCountInput
                        key={`fragment-${profile.get('key')}`}
                        label={profile.get('description')}
                        value={profile.get('operatorNote')}
                        onChange={(e) =>
                            setProfiles(
                                profiles.setIn(
                                    [index, 'operatorNote'],
                                    e.target.value,
                                ),
                            )
                        }
                        disabled={!channel.get('editable')}
                        maxLength={OPERATOR_NOTE_MAX_LENGTH}
                        characterCountFrom={OPERATOR_NOTE_COUNT_FROM}
                    />
                ))}

                <div className="flex justify-end">
                    <Button
                        color="primary"
                        className="min-w-max px-5"
                        onClick={handleSubmit}
                        disabled={
                            !channel.get('editable') || anyGreetingIsOverLimit
                        }
                    >
                        {i18next.t('navigate.save')}
                    </Button>
                </div>
            </div>
        </Page>
    );
};

export default PbxLiveChatGreeting;
